import { NavLink } from "react-router";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useShowLocalDeliveryToggle } from "@design-lab/shared/lib/config";
import { getPath } from "common/utils/ui/use_svg";
import { DeliveryDateText } from "./DeliveryDateText";
import { ChatNow } from "./ChatNow";
import { CartCounter } from "./CartCounter";
import { DeliveryInfo } from "./DeliveryInfo";
import { LocalDeliveryToggle } from "./LocalDeliveryToggle";
import { SavedProjectsButton } from "./SavedProjectsButton";
import { ShareDesignLink } from "./ShareDesignLink";
import { type DeliveryDateInfo } from "../model/HeaderModes";

export type HeaderProps = {
  deliveryDateInfo: DeliveryDateInfo;
};

type HeaderInternalProps = HeaderProps & {
  showLocalDeliveryToggle: boolean;
};

type LogoProps = {
  className?: string;
};

function Logo({ className = "w-8 h-6 lg:w-8 lg:h-8" }: LogoProps) {
  return (
    <NavLink data-testid="logo-link" to="/" reloadDocument>
      <svg className={className}>
        <use xlinkHref={`${getPath()}#j-logo`} />
      </svg>
    </NavLink>
  );
}

function DesktopHeader({ deliveryDateInfo, showLocalDeliveryToggle }: HeaderInternalProps) {
  return (
    <header className="bg-background flex w-full flex-col gap-2.5 px-4 py-3 drop-shadow-xl xl:px-8">
      <div className="flex items-center justify-between">
        <div className="flex min-h-9 w-full flex-1 flex-row items-center justify-between gap-3">
          <div className="flex flex-col items-center gap-4 xl:flex-row">
            <Logo />
            <SavedProjectsButton />
            {showLocalDeliveryToggle && (
              <div className="w-[210px]">
                <LocalDeliveryToggle />
              </div>
            )}
          </div>

          <DeliveryInfo deliveryDateInfo={deliveryDateInfo} />

          <div className="mr-2 flex flex-row items-center gap-2 lg:mr-0 lg:gap-4">
            <ShareDesignLink />
            <ChatNow />
            <CartCounter />
          </div>
        </div>
      </div>
    </header>
  );
}

function MobileHeader({ deliveryDateInfo, showLocalDeliveryToggle }: HeaderInternalProps) {
  const { isMobile } = useResponsive();

  return (
    <header className="bg-background flex w-full flex-col gap-1 px-2 py-1 drop-shadow-xl md:gap-2.5 md:py-2">
      <div className="flex items-center justify-between">
        <div className="flex min-h-9 w-full flex-1 flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo />
            <SavedProjectsButton />
            {!isMobile && showLocalDeliveryToggle && (
              <div className="max-w-40">
                <LocalDeliveryToggle />
              </div>
            )}
          </div>

          <DeliveryDateText className="text-center text-sm" deliveryDateInfo={deliveryDateInfo} />

          <div className="mr-2 flex flex-row items-center gap-2">
            <ShareDesignLink />
            <ChatNow />
            <CartCounter />
          </div>
        </div>
      </div>

      {isMobile && showLocalDeliveryToggle && (
        <div className="w-full md:hidden">
          <LocalDeliveryToggle />
        </div>
      )}
    </header>
  );
}

export function Header(props: HeaderProps) {
  const { isDesktop } = useResponsive(768, 1440);
  const showLocalDeliveryToggle = useShowLocalDeliveryToggle();

  return isDesktop ? (
    <DesktopHeader {...props} showLocalDeliveryToggle={showLocalDeliveryToggle} />
  ) : (
    <MobileHeader {...props} showLocalDeliveryToggle={showLocalDeliveryToggle} />
  );
}
